import React from "react";
import LegalPage from "@/components/site/LegalPage";

const SECTIONS = [
  {
    id: "introduction",
    heading: "Introduction",
    body: [
      { type: "p", text: "CoSec Global Ltd (\"CoSec Global\", \"we\", \"us\", or \"our\") is committed to protecting and respecting the privacy of every individual whose personal data we process. This Privacy Policy explains what personal information we collect when you use our websites, the CoSec App and the CoVerify identity verification service (together, the \"Services\"), how we use it, and the rights you have over your data." },
      { type: "p", text: "We process personal data in accordance with the UK General Data Protection Regulation (UK GDPR), the Data Protection Act 2018 and the Privacy and Electronic Communications Regulations (PECR). Our data controller is CoSec Global Ltd, registered in England and Wales at 20–22 Wenlock Road, London, N1 7GU." },
    ],
  },
  {
    id: "what-we-collect",
    heading: "What information we collect",
    body: [
      { type: "h3", text: "Information you give us" },
      { type: "list", items: [
        "Contact and identification data, name, business email, phone number, company, role.",
        "Account data, login credentials, role-based access permissions, audit log of actions you take inside CoSec App.",
        "Statutory data you upload into CoSec App, entity records, officer details, PSC information, share capital details.",
        "Identity verification data (CoVerify only), biometric document images, selfie liveness video, name, date of birth, address. CoVerify checks are processed by our identity verification sub-processor.",
        "Marketing preferences, whether you have opted in to product, regulatory or event communications.",
      ]},
      { type: "h3", text: "Information we collect automatically" },
      { type: "list", items: [
        "Device and connection data. IP address, browser type and version, operating system, time zone.",
        "Usage data, pages visited, features used, errors encountered. Used only to improve the Services.",
        "Cookies and similar technologies, see our separate Cookies Policy.",
      ]},
    ],
  },
  {
    id: "how-we-use",
    heading: "How we use your information",
    body: [
      { type: "p", text: "We use personal data for the following purposes, each with a clear lawful basis under the UK GDPR:" },
      { type: "list", items: [
        "To deliver the Services and perform our contract with you, including hosting your data, running identity verification checks, and providing customer support.",
        "To meet our legitimate interests, improving the Services, preventing fraud and abuse, securing our infrastructure, and developing new features.",
        "To comply with legal and regulatory obligations, including obligations under the Companies Act 2006, the Economic Crime and Corporate Transparency Act 2023, the Money Laundering Regulations 2017, and tax law.",
        "With your consent, for marketing communications you have opted in to, and for non-essential cookies.",
      ]},
    ],
  },
  {
    id: "sharing",
    heading: "Who we share your information with",
    body: [
      { type: "p", text: "We share personal data with the following categories of recipient:" },
      { type: "list", items: [
        "Sub-processors who help us deliver the Services, including Amazon Web Services (hosting), our identity verification sub-processor, and our customer support and email infrastructure providers.",
        "Companies House and other UK regulators, where you instruct us to submit statutory filings on your behalf.",
        "Professional advisors, our auditors, lawyers and bankers, under strict confidentiality obligations.",
        "Law enforcement, courts and regulators when we are legally required to disclose information.",
      ]},
      { type: "callout", text: "We do not sell personal data, and we do not share it with third-party advertisers." },
    ],
  },
  {
    id: "transfers",
    heading: "International data transfers",
    body: [
      { type: "p", text: "We primarily host customer data in the United Kingdom and the European Economic Area. Where personal data is transferred outside the UK / EEA, for example to a sub-processor in another jurisdiction, we rely on adequacy decisions, the UK International Data Transfer Agreement, or the EU Standard Contractual Clauses (with the UK Addendum) to ensure your data remains protected to UK standards." },
    ],
  },
  {
    id: "retention",
    heading: "How long we keep your information",
    body: [
      { type: "p", text: "We retain personal data only for as long as we need it to deliver the Services and meet our legal obligations." },
      { type: "list", items: [
        "Account and statutory data, retained for the life of your CoSec App account, plus six years after termination for tax and audit purposes.",
        "Identity verification records (CoVerify), retained for seven years to support the Money Laundering Regulations 2017 record-keeping requirement.",
        "Marketing data, retained until you withdraw consent.",
        "Support correspondence, retained for two years from your last interaction with us.",
      ]},
    ],
  },
  {
    id: "your-rights",
    heading: "Your rights",
    body: [
      { type: "p", text: "You have the following rights over your personal data:" },
      { type: "list", items: [
        "Right of access, request a copy of the personal data we hold about you.",
        "Right to rectification, ask us to correct inaccurate or incomplete data.",
        "Right to erasure, ask us to delete your data, subject to our legal retention obligations.",
        "Right to restriction, ask us to limit how we process your data.",
        "Right to data portability, request a copy of your data in a portable format.",
        "Right to object, object to processing based on our legitimate interests, and to direct marketing.",
        "Right to withdraw consent, at any time, where we rely on consent.",
      ]},
      { type: "p", text: "To exercise any of these rights, email privacy@cosecglobal.co. We will respond within one month. You also have the right to lodge a complaint with the UK Information Commissioner's Office (ICO) at ico.org.uk." },
    ],
  },
  {
    id: "security",
    heading: "How we keep your data secure",
    body: [
      { type: "p", text: "We are ISO 27001 and ISO 27701 certified and host customer data in encrypted AWS infrastructure. All data is encrypted in transit (TLS 1.2+) and at rest (AES-256). Access to production systems is restricted, logged and reviewed quarterly." },
    ],
  },
  {
    id: "changes",
    heading: "Changes to this policy",
    body: [
      { type: "p", text: "We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal or regulatory reasons. We will notify you of material changes by email or via the Services. The \"last updated\" date at the top of the policy will always reflect the latest version." },
    ],
  },
  {
    id: "contact",
    heading: "Contact us",
    body: [
      { type: "p", text: "Questions about this Privacy Policy, or about how we handle your personal data, can be sent to:" },
      { type: "p", text: "CoSec Global Ltd, 20–22 Wenlock Road, London, N1 7GU, United Kingdom." },
      { type: "p", text: "Email: privacy@cosecglobal.com &middot; Phone: +44 330 133 2696" },
    ],
  },
];

export default function PrivacyPage() {
  return (
    <LegalPage
      eyebrow="Privacy"
      title="Privacy Policy"
      updatedAt="08 March 2026"
      sections={SECTIONS}
      related={[
        { href: "/terms", label: "Terms of Service" },
        { href: "/cookies", label: "Cookies Policy" },
        { href: "/contact", label: "Contact our DPO" },
      ]}
    />
  );
}
